import LegalPageLayout from '../components/LegalPageLayout';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-4">
      <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
      <div className="space-y-4 text-sm leading-7 text-gray-600">{children}</div>
    </section>
  );
}

export default function SecurityOverview() {
  return (
    <LegalPageLayout
      title="Security Overview"
      subtitle="A plain-English summary of the safeguards Filie uses today, the limits of those safeguards, and how beta users can evaluate trust responsibly."
      lastUpdated="May 1, 2026"
    >
      <Section title="What This Page Is">
        <p>
          This page is intended to describe Filie&apos;s current beta security posture in clear language. It is not a
          guarantee of perfect security, and it does not replace the more complete legal terms and privacy policy.
        </p>
      </Section>

      <Section title="How Filie Handles Sensitive Tax Data">
        <ul className="list-disc space-y-2 pl-6">
          <li>Sensitive identifiers such as SSNs and EINs are extracted locally in Filie&apos;s current production pipeline.</li>
          <li>In the current pipeline, those identifiers are redacted from document images before those images are sent to an external AI provider for classification or analysis.</li>
          <li>Filie uses AI to read documents, but Filie&apos;s deterministic engine performs the tax math.</li>
          <li>You review extracted values before relying on a result.</li>
        </ul>
      </Section>

      <Section title="Storage and Encryption">
        <ul className="list-disc space-y-2 pl-6">
          <li>Traffic to Filie is protected with HTTPS/TLS.</li>
          <li>Uploaded documents are stored under server-generated object keys rather than user-supplied filenames.</li>
          <li>Documents are encrypted before storage and protected again by provider-side storage controls.</li>
          <li>Sensitive values stored in Filie&apos;s systems use field-level encryption and access controls.</li>
        </ul>
      </Section>

      <Section title="Account and Access Protections">
        <ul className="list-disc space-y-2 pl-6">
          <li>Passwords are stored as hashes rather than plaintext.</li>
          <li>Login attempts are rate limited.</li>
          <li>Sessions are revoked on logout, password reset, and password change.</li>
          <li>Administrative endpoints require authenticated admin access.</li>
        </ul>
      </Section>

      <Section title="Monitoring and Operations">
        <ul className="list-disc space-y-2 pl-6">
          <li>Filie uses production error monitoring on both the backend and frontend.</li>
          <li>We review operational failures during beta and treat security or trust regressions as high-priority issues.</li>
          <li>Uploaded document contents are not intended to be sent into routine error telemetry.</li>
        </ul>
      </Section>

      <Section title="What Filie Does Not Claim">
        <ul className="list-disc space-y-2 pl-6">
          <li>No internet-connected system can promise perfect security.</li>
          <li>Filie does not claim that data breaches are impossible.</li>
          <li>Filie does not claim that the current beta has completed a third-party audit or penetration test.</li>
        </ul>
      </Section>

      <Section title="How To Use Filie Safely During Beta">
        <ul className="list-disc space-y-2 pl-6">
          <li>Use a strong unique password for your Filie account.</li>
          <li>Review extracted values carefully before relying on them.</li>
          <li>Report anything confusing, suspicious, or incorrect through the beta feedback flow or by email.</li>
          <li>If you are not comfortable uploading a real document yet, start with a low-risk test file first.</li>
        </ul>
      </Section>

      <Section title="Report a Security Concern">
        <p>
          If you believe you found a security issue or a sensitive-data handling problem, email
          {' '}<a className="text-gray-900 underline" href="mailto:security@filie.ai">security@filie.ai</a>
          {' '}or{' '}
          <a className="text-gray-900 underline" href="mailto:support@filie.ai">support@filie.ai</a>.
        </p>
      </Section>
    </LegalPageLayout>
  );
}
